import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { IonContent, ModalController } from '@ionic/angular';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { CountriesPage } from '../../../countries/countries.page';
import { ContactPayload, Contacts } from '@capacitor-community/contacts';
import { Capacitor } from '@capacitor/core';
import { User } from '../../models/user.model';
import { UserService } from '../../../_core/services/user.service';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

const phoneNumberUtil = PhoneNumberUtil.getInstance();

export interface PermissionStatus {
    granted: boolean;
}

@Component({
    standalone: false,
    selector: 'app-select-user',
    templateUrl: './select-user.component.html',
    styleUrls: ['./select-user.component.scss'],
})
export class SelectUserComponent implements OnInit {
    @ViewChild('content') content: IonContent;

    form: UntypedFormGroup;
    users = [];
    formActive = false;
    contacts: ContactPayload[];

    authUser: User;

    constructor(
        private fb: UntypedFormBuilder,
        private modalCtrl: ModalController,
        private userService: UserService
    ) { }

    async ngOnInit() {
        const response = await lastValueFrom(this.userService.myProfile());
        this.authUser = response?.data as User;
        this.buildForm();
    }

    buildForm() {
        this.form = this.fb.group({
            name: [''],
            country_code: ['+1', [Validators.pattern('([+]{1})([0-9]{1,3})')]],
            phone: ['', [Validators.pattern('([1-9]{1})[0-9]{5,15}')]],
            email: ['', [Validators.email]],
        });

        this.form.get('name').valueChanges.subscribe(value => {
            this.findContacts(value);
        });

        this.getContacts();
        this.formActive = true;
    }

    async getContacts() {
        if (Capacitor.isNativePlatform()) {
            const contacts = (await Contacts.getContacts({
                projection: {
                    name: true,
                    phones: true,
                    emails: true
                }
            })).contacts;

            this.contacts = contacts
                .filter(contact =>
                    (contact.phones?.length > 0 || contact.emails?.length > 0) &&
                    (contact.name?.given?.length > 0 || contact.name?.family?.length > 0)
                );
        }
    }

    submit() {
        this.form.valid
            ? this.modalCtrl.dismiss(this.form.value)
            : this.handleErrors();
    }

    async handleErrors() {
        let errorMessage = '';
        if (this.form.get('country_code').invalid) {
            errorMessage = 'Country code must start with + followed by 1 to 3 digits';
        } else if (this.form.get('phone').invalid) {
            errorMessage = 'Phone must include area code and phone number. Digits only. If your first digit is 0 remove it.';
        } else if (this.form.get('email').invalid) {
            errorMessage = 'Please enter a valid email address.';
        } else if (this.form.errors?.phoneOrEmailRequired) {
            errorMessage = 'Please provide either a phone number or email address.';
        } else if (this.form.get('name').invalid) {
            errorMessage = 'Name is required.';
        }
    }

    findContacts(val: string) {
        if (Capacitor.isNativePlatform()) {
            this.users = [];
            this.contacts.forEach(contact => {
                const name = contact.name?.display;
                if (name && name.toLowerCase().includes(val.toLowerCase())) {
                    let phone = '';
                    let email = '';

                    if (contact.phones?.length > 0) {
                        const phoneNumber = contact.phones[0].number;
                        phone = phoneNumber.slice(0, 1) === '+' || phoneNumber.slice(0, 1) === '0'
                            ? phoneNumber.replace(/[^a-zA-Z0-9+]/g, '')
                            : phoneNumber.replace(/[^a-zA-Z0-9]/g, '');
                    }

                    if (contact.emails?.length > 0) {
                        email = contact.emails[0].address;
                    }

                    const contactData = {
                        name: name,
                        phone: phone,
                        email: email,
                    };
                    this.users.push(contactData);
                }

            });

            this.users.sort((a, b) => {
                const nameA = a.name?.toLowerCase(); // ignore upper and lowercase
                const nameB = b.name?.toLowerCase(); // ignore upper and lowercase

                if (nameA < nameB) {
                    return -1;
                } else if (nameA > nameB) {
                    return 1;
                } else {
                    return 0;
                }
            });

            const uniq = {};
            this.users = this.users.filter(obj => !uniq[obj.phone] && (uniq[obj.phone] = true));
        }

    }

    isDummyEmail(email: string) {
        if (!email || !email.includes("@")) return false;

        const [localPart] = email.split("@");
        return /^\+?\d{7,15}$/.test(localPart);
    }

    applyContactInfo(data) {
        if (data) {
            const isDummyEmail = this.isDummyEmail(data.email);
            this.form.get('name').setValue(data.name);

            if (!isDummyEmail && data.email) {
                this.form.get('email').setValue(data.email);
                this.form.get('phone').setValue('');
                this.form.get('country_code').setValue('');
            }
            else if (isDummyEmail && data.phone) {
                if (data.phone.charAt(0) === '+') {
                    const phoneNumber = phoneNumberUtil.parse(data.phone);
                    this.form.get('country_code').setValue('+' + phoneNumber.getCountryCode());
                    this.form.get('phone').setValue(phoneNumber.getNationalNumber());
                } else {
                    this.form.get('phone').setValue(data.phone);
                }
                this.form.get('email').setValue('');
            }
            else if (!data.email && data.phone) {
                if (data.phone.charAt(0) === '+') {
                    const phoneNumber = phoneNumberUtil.parse(data.phone);
                    this.form.get('country_code').setValue('+' + phoneNumber.getCountryCode());
                    this.form.get('phone').setValue(phoneNumber.getNationalNumber());
                } else {
                    this.form.get('phone').setValue(data.phone);
                }
                this.form.get('email').setValue('');
            }
            else {
                this.form.get('email').setValue('');
                this.form.get('phone').setValue('');
                this.form.get('country_code').setValue('');
            }

            this.users = [];
            this.content.scrollToTop(300)
        }
    }

    async getCountryCode() {
        const modal = await this.modalCtrl.create({
            component: CountriesPage
        });

        await modal.present();
        const { data } = await modal.onDidDismiss();

        if (data) {
            this.form.get('country_code').setValue(data);
        }
    }

    close() {
        this.modalCtrl.dismiss();
    }
}
